import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";
import { COMPANY } from "@/lib/company";
import { openCookiePreferences } from "@/components/CookieConsent";

export const Route = createFileRoute("/cookie-tajekoztato")({
  head: () => ({
    meta: [
      { title: "Cookie tájékoztató | LAKROSZ Autószerviz" },
      {
        name: "description",
        content:
          "Tájékoztató a LAKROSZ Autószerviz weboldalán használt sütikről (cookie-król) és azok kategóriáiról.",
      },
      { property: "og:title", content: "Cookie tájékoztató | LAKROSZ Autószerviz" },
      {
        property: "og:description",
        content: "A weboldalon használt sütik kategóriái és beállítási lehetőségei.",
      },
      { property: "og:url", content: "https://lakroszautoszerviz.com/cookie-tajekoztato" },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://lakroszautoszerviz.com/cookie-tajekoztato" },
    ],
  }),
  component: CookiePage,
});

function CookiePage() {
  return (
    <LegalLayout
      title="Cookie tájékoztató"
      subtitle={`Utolsó frissítés: ${COMPANY.lastUpdated}`}
    >
      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">A) Mi az a cookie?</h2>
        <p>
          A sütik (cookie-k) olyan kis adatcsomagok, amelyeket a meglátogatott weboldalak
          helyeznek el a böngésződben. Segítségükkel a weboldal felismeri a látogatást,
          megőrzi a beállításaidat, illetve statisztikai vagy marketing célokra használhatók.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">B) Cookie kategóriák</h2>

        <div className="rounded-2xl border border-border bg-surface p-6 mt-4">
          <h3 className="font-semibold text-lg text-primary">
            Szükséges / technikai sütik (mindig aktív)
          </h3>
          <p className="mt-2 text-sm">
            A weboldal alapvető működéséhez szükséges sütik (pl. cookie-beállítások mentése,
            biztonsági funkciók). Ezek nélkül az oldal nem működik megfelelően, ezért nem
            kapcsolhatók ki.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-6 mt-4">
          <h3 className="font-semibold text-lg">Analitikai sütik (alapból kikapcsolva)</h3>
          <p className="mt-2 text-sm">
            Anonim statisztikák gyűjtésére szolgálnak (látogatottság, leggyakrabban
            megnézett oldalak), hogy fejleszthessük a weboldalt. Csak akkor töltődnek be, ha
            kifejezetten elfogadod ezt a kategóriát.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-6 mt-4">
          <h3 className="font-semibold text-lg">Marketing sütik (alapból kikapcsolva)</h3>
          <p className="mt-2 text-sm">
            Hirdetések személyre szabására és hatékonyság-mérésre szolgálnak. Csak akkor
            töltődnek be, ha kifejezetten elfogadod ezt a kategóriát.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-6 mt-4">
          <h3 className="font-semibold text-lg">
            Külső szolgáltatók által használt sütik
          </h3>
          <p className="mt-2 text-sm">
            Beágyazott térképek, betűtípusok vagy videók megjelenítése során külső
            szolgáltatók is helyezhetnek el sütiket. Ezek a szolgáltatók saját adatkezelési
            szabályzattal rendelkeznek.
          </p>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">C) Cookie táblázat</h2>
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-sm border border-border rounded-xl overflow-hidden min-w-[640px]">
            <thead className="bg-surface">
              <tr className="text-left">
                <th className="p-3 border-b border-border">Süti neve</th>
                <th className="p-3 border-b border-border">Szolgáltató</th>
                <th className="p-3 border-b border-border">Cél</th>
                <th className="p-3 border-b border-border">Lejárati idő</th>
                <th className="p-3 border-b border-border">Típus</th>
                <th className="p-3 border-b border-border">Hozzájárulás</th>
              </tr>
            </thead>
            <tbody className="[&_td]:p-3 [&_td]:border-b [&_td]:border-border/60 [&_tr:last-child_td]:border-b-0">
              <tr>
                <td>
                  <code className="text-xs">lakrosz_consent</code>
                </td>
                <td>lakroszautoszerviz.com</td>
                <td>Süti-beállítások tárolása</td>
                <td>12 hónap</td>
                <td>Szükséges</td>
                <td>Nem szükséges</td>
              </tr>
              <tr className="text-muted-foreground">
                <td colSpan={6} className="text-xs italic">
                  A weboldal jelenleg nem használ külön analitikai vagy marketing célú sütiket.
                  A Google Search Console használata nem jár a weboldalon külön látogatói
                  nyomkövető süti elhelyezésével.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">D) Cookie beállítások módosítása</h2>
        <p>
          A süti-beállításaidat bármikor módosíthatod az alábbi gombbal, a footerben található
          „Cookie beállítások” linken, vagy a böngésződ beállításain keresztül.
        </p>
        <button
          type="button"
          onClick={() => openCookiePreferences()}
          className="mt-4 inline-flex items-center justify-center px-6 py-3 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold"
        >
          Süti beállítások megnyitása
        </button>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">E) Jogalap</h2>
        <p>
          A szükséges sütik jogalapja a weboldal működtetéséhez fűződő jogos érdek (GDPR 6.
          cikk (1) f) pont). A statisztikai és marketing sütik jogalapja az érintett
          hozzájárulása (GDPR 6. cikk (1) a) pont), amelyet a süti banneren keresztül adhatsz
          meg, és bármikor visszavonhatsz.
        </p>
      </section>

      <section className="text-xs text-muted-foreground mt-8">
        Adatkezelő: LAKROSZ Autószerviz · 3300 Eger, Vécseyvölgy utca 99. ·{" "}
        {COMPANY.contact.phone}
      </section>
    </LegalLayout>
  );
}
